import { useEffect, useRef, useState } from 'react';
import { clusterHealthcheck } from '../lib/api';

export type AgentHealthStatus = 'ok' | 'degraded' | 'down' | 'unknown';

export interface AgentHealth {
  status: AgentHealthStatus;
  latency: number | null;
  lastAt: number | null;
  samples: number[];
}

const POLL_MS = 10_000;
const MAX_SAMPLES = 24;

function deriveStatus(ok: boolean, health: unknown): AgentHealthStatus {
  if (!ok) return 'down';
  const s = (health as { status?: string } | undefined)?.status;
  return s === 'ok' ? 'ok' : 'degraded';
}

export function useAgentHealth(clusterId: string | null): AgentHealth {
  const [state, setState] = useState<AgentHealth>({
    status: 'unknown',
    latency: null,
    lastAt: null,
    samples: [],
  });
  const samplesRef = useRef<number[]>([]);

  useEffect(() => {
    samplesRef.current = [];
    setState({ status: 'unknown', latency: null, lastAt: null, samples: [] });
    if (!clusterId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const push = (status: AgentHealthStatus, latency: number | null) => {
      const ms = latency ?? 0;
      samplesRef.current = [...samplesRef.current.slice(-(MAX_SAMPLES - 1)), ms];
      setState({
        status,
        latency,
        lastAt: Date.now(),
        samples: samplesRef.current,
      });
    };

    const tick = async () => {
      const t0 = performance.now();
      try {
        const res = await clusterHealthcheck(clusterId);
        if (cancelled) return;
        const latency = Math.round(performance.now() - t0);
        push(deriveStatus(res.ok, res.health), res.ok ? latency : null);
      } catch {
        if (cancelled) return;
        push('down', null);
      }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clusterId]);

  return state;
}
